import React, { useState, useRef, useEffect, useCallback } from 'react';
import styled from 'styled-components';

// ── Types ────────────────────────────────────────────────────────────────────
interface Keyframe { frame: number; x: number; y: number; w?: number; h?: number; }
interface Region {
  id: string;
  label: string;
  frameStart: number;
  frameEnd: number | null;
  x: number; y: number; w: number; h: number;
  color: string;
  keyframes?: Keyframe[];
}
type HandleType = 'move' | 'tl' | 'tr' | 'bl' | 'br';
interface Interaction {
  regionId: string;
  handle: HandleType;
  startMouse: { x: number; y: number };
  origRect: { x: number; y: number; w: number; h: number };
}

const COLORS = ['#ef4444', '#f97316', '#eab308', '#22c55e', '#3b82f6', '#a855f7', '#ec4899', '#06b6d4'];
const HANDLE_HIT = 14;   // hit-test radius in canvas px
const HANDLE_DRAW = 8;   // drawn handle square half-size

const genId = () => Math.random().toString(36).slice(2, 9);
const fmtTime = (sec: number) => {
  const m = Math.floor(sec / 60);
  const s = String(Math.floor(sec % 60)).padStart(2, '0');
  const cs = String(Math.round((sec % 1) * 100)).padStart(2, '0');
  return `${m}:${s}.${cs}`;
};

function getStateAtFrame(r: Region, frame: number): { x: number; y: number; w: number; h: number } {
  const kf = r.keyframes;
  if (!kf || kf.length === 0) return { x: r.x, y: r.y, w: r.w, h: r.h };
  const sorted = [...kf].sort((a, b) => a.frame - b.frame);
  const first = sorted[0], last = sorted[sorted.length - 1];
  if (frame <= first.frame) return { x: first.x, y: first.y, w: first.w ?? r.w, h: first.h ?? r.h };
  if (frame >= last.frame)  return { x: last.x,  y: last.y,  w: last.w  ?? r.w, h: last.h  ?? r.h };
  for (let i = 0; i < sorted.length - 1; i++) {
    const k0 = sorted[i], k1 = sorted[i + 1];
    if (frame >= k0.frame && frame <= k1.frame) {
      const t = (frame - k0.frame) / (k1.frame - k0.frame);
      const lerp = (a: number, b: number) => Math.round(a + t * (b - a));
      return {
        x: lerp(k0.x, k1.x), y: lerp(k0.y, k1.y),
        w: lerp(k0.w ?? r.w, k1.w ?? r.w),
        h: lerp(k0.h ?? r.h, k1.h ?? r.h),
      };
    }
  }
  return { x: r.x, y: r.y, w: r.w, h: r.h };
}

function computeRect(ia: Interaction, mouse: { x: number; y: number }) {
  const { handle, startMouse: sm, origRect: o } = ia;
  const dx = mouse.x - sm.x, dy = mouse.y - sm.y;
  const MIN = 20;
  switch (handle) {
    case 'move': return { x: Math.round(o.x + dx), y: Math.round(o.y + dy), w: o.w, h: o.h };
    case 'tl':   return { x: Math.round(o.x + dx), y: Math.round(o.y + dy), w: Math.max(MIN, Math.round(o.w - dx)), h: Math.max(MIN, Math.round(o.h - dy)) };
    case 'tr':   return { x: o.x,                  y: Math.round(o.y + dy), w: Math.max(MIN, Math.round(o.w + dx)), h: Math.max(MIN, Math.round(o.h - dy)) };
    case 'bl':   return { x: Math.round(o.x + dx), y: o.y,                  w: Math.max(MIN, Math.round(o.w - dx)), h: Math.max(MIN, Math.round(o.h + dy)) };
    case 'br':   return { x: o.x,                  y: o.y,                  w: Math.max(MIN, Math.round(o.w + dx)), h: Math.max(MIN, Math.round(o.h + dy)) };
  }
}

const HANDLE_CURSORS: Record<HandleType, string> = {
  move: 'grab', tl: 'nwse-resize', tr: 'nesw-resize', bl: 'nesw-resize', br: 'nwse-resize',
};

// ── Styles ───────────────────────────────────────────────────────────────────
const Page = styled.div`
  height: 100vh; background: #0f172a; color: #e2e8f0;
  font-family: system-ui, -apple-system, sans-serif;
  display: flex; flex-direction: column; overflow: hidden;
`;
const TopBar = styled.div`
  padding: 11px 20px; background: #1e293b; border-bottom: 1px solid #334155;
  display: flex; align-items: center; gap: 14px; flex-shrink: 0;
`;
const TopTitle = styled.h1`font-size: 15px; font-weight: 700; color: #f1f5f9; margin: 0;`;
const Body = styled.div`flex: 1; display: flex; overflow: hidden;`;
const VideoArea = styled.div`
  flex: 1; display: flex; flex-direction: column;
  padding: 12px; gap: 10px; min-width: 0; overflow: hidden;
`;
const VideoWrapper = styled.div<{ $aspect: number }>`
  position: relative; background: #000; border-radius: 8px; overflow: hidden;
  width: 100%; padding-top: ${p => (1 / p.$aspect) * 100}%; flex-shrink: 0;
`;
const StyledVideo = styled.video`position: absolute; inset: 0; width: 100%; height: 100%; display: block;`;
const Overlay = styled.canvas`position: absolute; inset: 0; width: 100%; height: 100%; display: block;`;
const DropArea = styled.div`
  flex: 1; display: flex; flex-direction: column; align-items: center;
  justify-content: center; gap: 12px; border: 2px dashed #334155;
  border-radius: 12px; cursor: pointer; color: #64748b; font-size: 14px;
  transition: border-color 0.15s, color 0.15s;
  &:hover { border-color: #3b82f6; color: #94a3b8; }
  &[data-drag='true'] { border-color: #3b82f6; background: #1e293b; }
`;
const FrameBar = styled.div`
  display: flex; align-items: center; gap: 6px; background: #1e293b;
  border: 1px solid #334155; border-radius: 8px; padding: 8px 12px; flex-shrink: 0;
`;
const IBtn = styled.button`
  background: #334155; color: #e2e8f0; border: none; border-radius: 5px;
  padding: 5px 10px; font-size: 12px; cursor: pointer;
  &:hover { background: #475569; }
  &:disabled { opacity: 0.3; cursor: default; }
`;
const Scrubber = styled.input`flex: 1; accent-color: #3b82f6; cursor: pointer;`;
const FrameText = styled.span`
  font-size: 11px; color: #94a3b8; font-family: 'Menlo', monospace;
  white-space: nowrap; min-width: 140px; text-align: right;
`;
const FpsInput = styled.input`
  width: 46px; background: #0f172a; border: 1px solid #334155; border-radius: 5px;
  color: #f1f5f9; padding: 4px 6px; font-size: 11px; text-align: center;
`;
const StepSelect = styled.select`
  background: #0f172a; border: 1px solid #334155; border-radius: 5px;
  color: #f1f5f9; padding: 4px 6px; font-size: 11px; cursor: pointer;
`;
const Sidebar = styled.div`
  width: 320px; flex-shrink: 0; background: #1e293b;
  border-left: 1px solid #334155; display: flex; flex-direction: column; overflow: hidden;
`;
const SideTop = styled.div`
  padding: 12px; border-bottom: 1px solid #334155;
  display: flex; flex-direction: column; gap: 8px;
`;
const Hint = styled.div`font-size: 10px; color: #475569; line-height: 1.5;`;
const RegionScroll = styled.div`flex: 1; overflow-y: auto; padding: 8px;`;
const SideSectionLabel = styled.div`
  font-size: 10px; font-weight: 700; text-transform: uppercase;
  letter-spacing: 0.08em; color: #475569; padding: 8px 12px 4px;
`;
const RegionItem = styled.div<{ $sel: boolean; $color: string; $open: boolean }>`
  background: #0f172a;
  border: 2px solid ${p => p.$sel ? p.$color : p.$open ? p.$color + '55' : '#1e293b'};
  border-radius: 8px; padding: 9px 11px; margin-bottom: 6px; cursor: pointer;
  transition: border-color 0.1s;
  &:hover { border-color: ${p => p.$color}88; }
`;
const RHead = styled.div`display: flex; align-items: center; gap: 6px; margin-bottom: 5px;`;
const Dot = styled.div<{ $c: string }>`width: 9px; height: 9px; border-radius: 50%; background: ${p => p.$c}; flex-shrink: 0;`;
const RInput = styled.input`
  flex: 1; background: transparent; border: none; color: #f1f5f9;
  font-size: 13px; font-weight: 600; outline: none;
  &::placeholder { color: #475569; }
`;
const RMeta = styled.div`font-size: 10px; color: #64748b; font-family: 'Menlo', monospace; line-height: 1.7; white-space: pre;`;
const RActions = styled.div`display: flex; gap: 5px; margin-top: 7px; flex-wrap: wrap; align-items: center;`;
const SmBtn = styled.button<{ $color?: string }>`
  padding: 3px 8px; border-radius: 5px;
  border: 1px solid ${p => p.$color || '#334155'};
  background: transparent; color: ${p => p.$color || '#94a3b8'};
  font-size: 11px; font-weight: 600; cursor: pointer;
  &:hover { background: ${p => p.$color || '#334155'}22; }
`;
const Btn = styled.button<{ $v?: 'primary' | 'green' | 'red' | 'active' | 'orange' }>`
  padding: 8px 12px; border-radius: 7px; border: none;
  font-size: 13px; font-weight: 600; cursor: pointer;
  background: ${p =>
    p.$v === 'primary' ? '#3b82f6' : p.$v === 'green'  ? '#16a34a' :
    p.$v === 'red'     ? '#dc2626' : p.$v === 'active' ? '#0ea5e9' :
    p.$v === 'orange'  ? '#d97706' : '#334155'};
  color: #fff; width: 100%; transition: opacity 0.15s;
  &:hover { opacity: 0.85; }
  &:disabled { opacity: 0.35; cursor: default; }
`;
const Row = styled.div`display: flex; gap: 6px; align-items: center;`;
const TrackBar = styled.div`
  background: #0f172a; border: 1px solid #f97316; border-radius: 7px;
  padding: 8px 12px; display: flex; flex-direction: column; gap: 4px;
`;
const TrackProgress = styled.div<{ $pct: number }>`
  height: 3px; background: #334155; border-radius: 2px; overflow: hidden;
  &::after { content: ''; display: block; height: 100%; width: ${p => p.$pct}%; background: #f97316; transition: width 0.1s; }
`;
const EndHereBtn = styled.button`
  padding: 5px 10px; border-radius: 5px; width: 100%;
  border: 1px solid #ef4444; background: #ef444418;
  color: #ef4444; font-size: 12px; font-weight: 700; cursor: pointer;
  &:hover { background: #ef444430; }
`;
const OpenBadge = styled.span`
  font-size: 9px; font-weight: 700; background: #f97316; color: #000;
  border-radius: 3px; padding: 1px 5px; letter-spacing: 0.05em;
`;
const PipelineBox = styled.div`
  border-top: 1px solid #334155; padding: 10px 12px;
  display: flex; flex-direction: column; gap: 6px; flex-shrink: 0;
`;
const CmdField = styled.div`
  background: #0f172a; border: 1px solid #334155; border-radius: 6px;
  padding: 7px 10px; font-size: 10px; color: #94a3b8;
  font-family: 'Menlo', monospace; white-space: pre-wrap; word-break: break-all;
  cursor: text; user-select: all;
`;
const CopySmBtn = styled.button`
  align-self: flex-end; padding: 3px 9px; border-radius: 5px;
  border: 1px solid #334155; background: transparent; color: #64748b;
  font-size: 10px; font-weight: 600; cursor: pointer;
  &:hover { color: #94a3b8; }
`;
const GenerateBox = styled.div`
  border-top: 1px solid #1e3a5f; background: #0a1628;
  padding: 10px 12px; display: flex; flex-direction: column; gap: 7px; flex-shrink: 0;
`;
const GenLabel = styled.div`
  font-size: 10px; font-weight: 700; text-transform: uppercase;
  letter-spacing: 0.07em; color: #3b82f6;
`;
const GenInput = styled.input`
  background: #0f172a; border: 1px solid #334155; border-radius: 6px;
  color: #e2e8f0; font-size: 11px; padding: 5px 8px; width: 100%; box-sizing: border-box;
  font-family: 'Menlo', monospace;
  &::placeholder { color: #475569; }
`;

// ── Component ─────────────────────────────────────────────────────────────────
const VideoRedactor: React.FC = () => {
  const [videoUrl, setVideoUrl]         = useState<string | null>(null);
  const [videoName, setVideoName]       = useState('');
  const [videoDims, setVideoDims]       = useState({ w: 1920, h: 1080 });
  const [videoAspect, setVideoAspect]   = useState(16 / 9);
  const [fps, setFps]                   = useState(30);
  const [totalFrames, setTotalFrames]   = useState(0);
  const [currentFrame, setCurrentFrame] = useState(0);
  const [regions, setRegions]           = useState<Region[]>([]);
  const [selectedId, setSelectedId]     = useState<string | null>(null);
  const [drawMode, setDrawMode]         = useState(false);
  const [dragStart, setDragStart]       = useState<{ x: number; y: number } | null>(null);
  const [draftRect, setDraftRect]       = useState<{ x: number; y: number; w: number; h: number } | null>(null);
  const [isDragOver, setIsDragOver]     = useState(false);
  const [isTracking, setIsTracking]     = useState(false);
  const [trackingRegionId, setTrackingRegionId] = useState<string | null>(null);
  const [trackingFrame, setTrackingFrame]       = useState(0);
  const [interaction, setInteraction]   = useState<Interaction | null>(null);
  const [interactionRect, setInteractionRect] = useState<{ x: number; y: number; w: number; h: number } | null>(null);
  const [hoverHandle, setHoverHandle]   = useState<HandleType | null>(null);
  const [savedPath, setSavedPath]       = useState<string | null>(null);
  const [copiedCmd, setCopiedCmd]       = useState<string | null>(null);
  const [genAnnotName, setGenAnnotName]   = useState('');
  const [genDemoName, setGenDemoName]     = useState('');
  const [stepInterval, setStepInterval]   = useState(1.0);  // seconds per ◀◀/▶▶ step

  const videoRef      = useRef<HTMLVideoElement>(null);
  const canvasRef     = useRef<HTMLCanvasElement>(null);
  const fileInputRef  = useRef<HTMLInputElement>(null);
  const loadAnnRef    = useRef<HTMLInputElement>(null);
  const offscreen    = useRef(document.createElement('canvas'));
  const abortTracking = useRef(false);
  const fpsRef       = useRef(fps);
  const totalFramesRef = useRef(totalFrames);
  useEffect(() => { fpsRef.current = fps; }, [fps]);
  useEffect(() => { totalFramesRef.current = totalFrames; }, [totalFrames]);

  useEffect(() => {
    if (!videoName) return;
    setGenDemoName(videoName.replace(/\.[^.]+$/, '').replace(/_raw$/, ''));
  }, [videoName]);

  // ── File loading ─────────────────────────────────────────────────────────
  const loadFile = useCallback((file: File) => {
    abortTracking.current = true;
    setVideoUrl(prev => { if (prev) URL.revokeObjectURL(prev); return URL.createObjectURL(file); });
    setVideoName(file.name);
    setRegions([]); setSelectedId(null); setCurrentFrame(0);
    setDrawMode(false); setIsTracking(false); setSavedPath(null);
  }, []);

  const onFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0]; if (f) loadFile(f);
  };

  const loadAnnotationsFromJson = useCallback((jsonText: string) => {
    try {
      const data = JSON.parse(jsonText);
      if (data.fps)  setFps(data.fps);
      if (data.videoWidth && data.videoHeight) setVideoDims({ w: data.videoWidth, h: data.videoHeight });
      const loaded: Region[] = (data.regions ?? []).map((r: any, i: number) => ({
        id:         r.id ?? genId(),
        label:      r.label ?? 'redact',
        frameStart: r.frameStart ?? 0,
        frameEnd:   r.frameEnd   ?? null,
        x: r.x, y: r.y, w: r.w, h: r.h,
        color: r.color ?? COLORS[i % COLORS.length],
        keyframes:  r.keyframes ?? undefined,
      }));
      setRegions(loaded);
      setSelectedId(null);
    } catch (err) {
      alert('Failed to parse annotations JSON');
    }
  }, []);
  const onDrop = (e: React.DragEvent) => {
    e.preventDefault(); setIsDragOver(false);
    const f = e.dataTransfer.files[0];
    if (f?.type.startsWith('video/')) loadFile(f);
  };

  // ── Video metadata ───────────────────────────────────────────────────────
  useEffect(() => {
    const video = videoRef.current; if (!video || !videoUrl) return;
    const onMeta = () => {
      const w = video.videoWidth, h = video.videoHeight;
      const total = Math.round(video.duration * fps);
      setVideoDims({ w, h }); setVideoAspect(w / h); setTotalFrames(total);
      offscreen.current.width = w; offscreen.current.height = h;
      const canvas = canvasRef.current;
      if (canvas) { canvas.width = w; canvas.height = h; }
    };
    video.addEventListener('loadedmetadata', onMeta);
    return () => video.removeEventListener('loadedmetadata', onMeta);
  }, [videoUrl, fps]);

  // ── Seeking ──────────────────────────────────────────────────────────────
  const seekToFrame = useCallback((frame: number) => {
    const video = videoRef.current;
    if (!video || totalFramesRef.current === 0) return;
    const c = Math.max(0, Math.min(frame, totalFramesRef.current - 1));
    video.currentTime = c / fpsRef.current;
    setCurrentFrame(c);
  }, []);

  const seekToFrameAsync = useCallback((frame: number): Promise<void> =>
    new Promise(resolve => {
      const video = videoRef.current!;
      const c = Math.max(0, Math.min(frame, totalFramesRef.current - 1));
      video.addEventListener('seeked', resolve, { once: true });
      video.currentTime = c / fpsRef.current;
    }), []);

  useEffect(() => {
    const video = videoRef.current; if (!video) return;
    const fn = () => setCurrentFrame(Math.round(video.currentTime * fps));
    video.addEventListener('seeked', fn);
    return () => video.removeEventListener('seeked', fn);
  }, [fps]);

  // ── Keyboard ─────────────────────────────────────────────────────────────
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement || isTracking) return;
      if (!videoUrl) return;
      const step = Math.max(1, Math.round(stepInterval * fpsRef.current));
      if (e.key === 'ArrowLeft')  { e.preventDefault(); seekToFrame(currentFrame - (e.shiftKey ? step : 1)); }
      if (e.key === 'ArrowRight') { e.preventDefault(); seekToFrame(currentFrame + (e.shiftKey ? step : 1)); }
      if (e.key === 'Escape') { setDrawMode(false); setDraftRect(null); setDragStart(null); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [videoUrl, currentFrame, seekToFrame, isTracking, stepInterval]);

  // ── Canvas drawing ───────────────────────────────────────────────────────
  useEffect(() => {
    const canvas = canvasRef.current; if (!canvas || !videoUrl) return;
    const ctx = canvas.getContext('2d'); if (!ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    for (const r of regions) {
      const isOpen = r.frameEnd === null;
      const active = currentFrame >= r.frameStart && (isOpen || currentFrame <= r.frameEnd!);
      const sel = r.id === selectedId;
      const tracking = r.id === trackingRegionId;
      const isInteracting = interaction?.regionId === r.id;
      const state = isInteracting && interactionRect ? interactionRect : getStateAtFrame(r, currentFrame);

      ctx.save();
      ctx.globalAlpha = active ? 1 : 0.22;
      ctx.strokeStyle = tracking ? '#f97316' : r.color;
      ctx.lineWidth = sel || tracking ? 3 : 2;
      ctx.setLineDash(active ? [] : [6, 4]);
      ctx.fillStyle = (tracking ? '#f97316' : r.color) + (isOpen && active ? '40' : '28');
      ctx.fillRect(state.x, state.y, state.w, state.h);
      ctx.strokeRect(state.x, state.y, state.w, state.h);

      if (active || sel) {
        ctx.globalAlpha = 1; ctx.setLineDash([]);
        const tag = (r.label || 'redact') + (isOpen ? ' ●' : '');
        const fs = Math.max(12, Math.round(canvas.height / 60));
        ctx.font = `bold ${fs}px system-ui`;
        const tw = ctx.measureText(tag).width;
        const ty = state.y > fs + 4 ? state.y - fs - 4 : state.y + state.h + 4;
        ctx.fillStyle = tracking ? '#f97316' : r.color;
        ctx.fillRect(state.x, ty, tw + 10, fs + 6);
        ctx.fillStyle = '#fff';
        ctx.fillText(tag, state.x + 5, ty + fs);
      }

      // Resize handles on selected active region
      if (sel && active) {
        ctx.globalAlpha = 1; ctx.setLineDash([]);
        const corners = [
          [state.x,            state.y           ],
          [state.x + state.w,  state.y           ],
          [state.x,            state.y + state.h ],
          [state.x + state.w,  state.y + state.h ],
        ];
        for (const [hx, hy] of corners) {
          ctx.fillStyle = '#fff';
          ctx.fillRect(hx - HANDLE_DRAW, hy - HANDLE_DRAW, HANDLE_DRAW * 2, HANDLE_DRAW * 2);
          ctx.strokeStyle = r.color;
          ctx.lineWidth = 2;
          ctx.strokeRect(hx - HANDLE_DRAW, hy - HANDLE_DRAW, HANDLE_DRAW * 2, HANDLE_DRAW * 2);
        }
      }
      ctx.restore();
    }

    if (draftRect) {
      ctx.save(); ctx.strokeStyle = '#fff'; ctx.lineWidth = 2;
      ctx.setLineDash([5, 4]); ctx.strokeRect(draftRect.x, draftRect.y, draftRect.w, draftRect.h);
      ctx.restore();
    }
  }, [currentFrame, regions, selectedId, draftRect, videoUrl, trackingRegionId, interaction, interactionRect]);

  // ── Tracking (optional re-track) ─────────────────────────────────────────
  const trackRegionForward = useCallback(async (regionId: string, r: Region) => {
    abortTracking.current = false;
    setIsTracking(true); setTrackingRegionId(regionId);
    const video = videoRef.current!;
    const off = offscreen.current;
    const offCtx = off.getContext('2d')!;
    const vw = off.width, vh = off.height;
    if (vw < 1 || vh < 1) { setIsTracking(false); setTrackingRegionId(null); return; }

    await seekToFrameAsync(r.frameStart);
    offCtx.drawImage(video, 0, 0);
    const { x: sx, y: sy, w: sw, h: sh } = getStateAtFrame(r, r.frameStart);
    const tmpl = new Uint8ClampedArray(
      offCtx.getImageData(Math.round(sx), Math.round(sy), Math.max(1, Math.round(sw)), Math.max(1, Math.round(sh))).data
    );

    const keyframes: Keyframe[] = [{ frame: r.frameStart, x: sx, y: sy, w: sw, h: sh }];
    let lastX = sx, lastY = sy;
    const STEP = 2, SAD_GONE = 22, POS_THRESH = 8, PAD = 60;

    const matchTemplate = (ctx: CanvasRenderingContext2D, tw: number, th: number, lx: number, ly: number) => {
      const msx = Math.max(0, Math.round(lx - PAD)), msy = Math.max(0, Math.round(ly - PAD));
      const tw2 = Math.max(1, Math.round(tw)), th2 = Math.max(1, Math.round(th));
      const msw = Math.min(vw - msx, tw2 + PAD * 2), msh = Math.min(vh - msy, th2 + PAD * 2);
      if (msw < tw2 || msh < th2) return { x: lx, y: ly, sad: 999 };
      const sd = ctx.getImageData(Math.round(msx), Math.round(msy), Math.round(msw), Math.round(msh)).data;
      const GRID = 3, SAMPLE = 3;
      let bestSad = Infinity, bestDx = 0, bestDy = 0;
      for (let dy = 0; dy <= msh - th2; dy += GRID)
        for (let dx = 0; dx <= msw - tw2; dx += GRID) {
          let sum = 0;
          for (let py = 0; py < th2; py += SAMPLE)
            for (let px = 0; px < tw2; px += SAMPLE) {
              const ti = (py * tw2 + px) * 4, si = ((dy + py) * msw + (dx + px)) * 4;
              sum += Math.abs((tmpl[ti] + tmpl[ti+1] + tmpl[ti+2]) / 3 - (sd[si] + sd[si+1] + sd[si+2]) / 3);
            }
          if (sum < bestSad) { bestSad = sum; bestDx = dx; bestDy = dy; }
        }
      const n = Math.ceil(tw2 / SAMPLE) * Math.ceil(th2 / SAMPLE);
      return { x: msx + bestDx, y: msy + bestDy, sad: bestSad / n };
    };

    for (let f = r.frameStart + STEP; f < totalFramesRef.current; f += STEP) {
      if (abortTracking.current) break;
      setTrackingFrame(f);
      await seekToFrameAsync(f);
      if (abortTracking.current) break;
      offCtx.drawImage(video, 0, 0);
      const { x: nx, y: ny, sad } = matchTemplate(offCtx, sw, sh, lastX, lastY);
      if (sad > SAD_GONE) break;
      if (Math.abs(nx - lastX) > POS_THRESH || Math.abs(ny - lastY) > POS_THRESH)
        keyframes.push({ frame: f, x: nx, y: ny, w: sw, h: sh });
      lastX = nx; lastY = ny;
    }

    setRegions(prev => prev.map(reg => reg.id !== regionId ? reg : {
      ...reg, keyframes: keyframes.length > 1 ? keyframes : reg.keyframes,
    }));
    setIsTracking(false); setTrackingRegionId(null);
    seekToFrame(r.frameStart);
  }, [seekToFrameAsync, seekToFrame]);

  // ── Canvas mouse helpers ─────────────────────────────────────────────────
  const toNative = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const c = canvasRef.current!;
    const rect = c.getBoundingClientRect();
    return { x: ((e.clientX - rect.left) / rect.width) * c.width, y: ((e.clientY - rect.top) / rect.height) * c.height };
  };

  const getHandleAt = (mx: number, my: number, state: { x: number; y: number; w: number; h: number }): HandleType | null => {
    const { x, y, w, h } = state;
    const corners: [HandleType, number, number][] = [
      ['tl', x,     y    ], ['tr', x + w, y    ],
      ['bl', x,     y + h], ['br', x + w, y + h],
    ];
    for (const [type, cx, cy] of corners)
      if (Math.abs(mx - cx) <= HANDLE_HIT && Math.abs(my - cy) <= HANDLE_HIT) return type;
    return null;
  };

  const getActiveRegionAt = (mx: number, my: number) =>
    [...regions].reverse().find(r => {
      if (currentFrame < r.frameStart || (r.frameEnd !== null && currentFrame > r.frameEnd)) return false;
      const s = getStateAtFrame(r, currentFrame);
      return mx >= s.x && mx <= s.x + s.w && my >= s.y && my <= s.y + s.h;
    });

  // ── Mouse events ─────────────────────────────────────────────────────────
  const onMouseDown = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (isTracking) return;
    const { x, y } = toNative(e);

    if (drawMode) { setDragStart({ x, y }); return; }

    // Check handles on selected region first
    if (selectedId) {
      const selRegion = regions.find(r => r.id === selectedId);
      if (selRegion) {
        const isActive = currentFrame >= selRegion.frameStart && (selRegion.frameEnd === null || currentFrame <= selRegion.frameEnd);
        if (isActive) {
          const state = getStateAtFrame(selRegion, currentFrame);
          const handle = getHandleAt(x, y, state);
          if (handle) {
            setInteraction({ regionId: selectedId, handle, startMouse: { x, y }, origRect: state });
            setInteractionRect(state);
            return;
          }
        }
      }
    }

    const hit = getActiveRegionAt(x, y);
    if (hit) {
      setSelectedId(hit.id);
      const state = getStateAtFrame(hit, currentFrame);
      setInteraction({ regionId: hit.id, handle: 'move', startMouse: { x, y }, origRect: state });
      setInteractionRect(state);
    } else {
      setSelectedId(null);
    }
  };

  const onMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const { x, y } = toNative(e);

    if (drawMode && dragStart) {
      setDraftRect({ x: Math.min(dragStart.x, x), y: Math.min(dragStart.y, y), w: Math.abs(x - dragStart.x), h: Math.abs(y - dragStart.y) });
      return;
    }

    if (interaction) {
      setInteractionRect(computeRect(interaction, { x, y }));
      return;
    }

    // Hover detection for cursor
    if (selectedId) {
      const selRegion = regions.find(r => r.id === selectedId);
      if (selRegion) {
        const isActive = currentFrame >= selRegion.frameStart && (selRegion.frameEnd === null || currentFrame <= selRegion.frameEnd);
        if (isActive) {
          const state = getStateAtFrame(selRegion, currentFrame);
          const handle = getHandleAt(x, y, state);
          setHoverHandle(handle ?? (getActiveRegionAt(x, y) ? 'move' : null));
          return;
        }
      }
    }
    setHoverHandle(getActiveRegionAt(x, y) ? 'move' : null);
  };

  const onMouseUp = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const { x, y } = toNative(e);

    if (drawMode && dragStart) {
      const rx = Math.min(dragStart.x, x), ry = Math.min(dragStart.y, y);
      const rw = Math.abs(x - dragStart.x), rh = Math.abs(y - dragStart.y);
      setDragStart(null); setDraftRect(null);
      if (rw < 8 || rh < 8) return;
      const color = COLORS[regions.length % COLORS.length];
      const id = genId();
      const newRegion: Region = {
        id, label: 'redact', frameStart: currentFrame, frameEnd: null,
        x: Math.round(rx), y: Math.round(ry), w: Math.round(rw), h: Math.round(rh), color,
        keyframes: [{ frame: currentFrame, x: Math.round(rx), y: Math.round(ry), w: Math.round(rw), h: Math.round(rh) }],
      };
      setRegions(prev => [...prev, newRegion]);
      setSelectedId(id);
      setDrawMode(false);
      return;
    }

    if (interaction && interactionRect) {
      const { regionId, origRect } = interaction;
      const changed =
        Math.abs(interactionRect.x - origRect.x) > 2 || Math.abs(interactionRect.y - origRect.y) > 2 ||
        Math.abs(interactionRect.w - origRect.w) > 2 || Math.abs(interactionRect.h - origRect.h) > 2;

      if (changed) {
        const newKf: Keyframe = { frame: currentFrame, ...interactionRect };
        setRegions(prev => prev.map(r => {
          if (r.id !== regionId) return r;
          const existing = r.keyframes ? [...r.keyframes] : [{ frame: r.frameStart, x: r.x, y: r.y, w: r.w, h: r.h }];
          const idx = existing.findIndex(k => k.frame === currentFrame);
          if (idx >= 0) existing[idx] = newKf; else { existing.push(newKf); existing.sort((a, b) => a.frame - b.frame); }
          return { ...r, keyframes: existing };
        }));
      }
      setInteraction(null); setInteractionRect(null);
    }
  };

  const onMouseLeave = () => {
    setHoverHandle(null);
    if (interaction) { setInteraction(null); setInteractionRect(null); }
  };

  // ── Region ops ───────────────────────────────────────────────────────────
  const updateLabel = (id: string, label: string) =>
    setRegions(prev => prev.map(r => r.id === id ? { ...r, label } : r));
  const markIn  = () => setRegions(prev => prev.map(r => r.id === selectedId ? { ...r, frameStart: currentFrame } : r));
  const markOut = () => setRegions(prev => prev.map(r => r.id === selectedId ? { ...r, frameEnd: currentFrame } : r));
  const endHere = () => setRegions(prev => prev.map(r => r.id === selectedId ? { ...r, frameEnd: currentFrame } : r));
  const deleteRegion = (id: string) => { setRegions(prev => prev.filter(r => r.id !== id)); if (selectedId === id) setSelectedId(null); };

  // ── Save ─────────────────────────────────────────────────────────────────
  const buildAnnotationData = () => {
    const base = videoName.replace(/\.[^.]+$/, '');
    return {
      demo: base, fps, videoWidth: videoDims.w, videoHeight: videoDims.h,
      regions: regions.map(({ id, label, frameStart, frameEnd, x, y, w, h, keyframes }) => ({
        id, label, frameStart, frameEnd: frameEnd ?? totalFrames - 1,
        x, y, w, h,
        ...(keyframes && keyframes.length > 0 ? { keyframes } : {}),
      })),
    };
  };

  const saveAnnotations = async () => {
    const data = buildAnnotationData();
    const json = JSON.stringify(data, null, 2);
    const suggestedName = `${data.demo}_blur_annotations.json`;
    if ('showSaveFilePicker' in window) {
      try {
        const handle = await (window as any).showSaveFilePicker({ suggestedName, types: [{ description: 'JSON Annotations', accept: { 'application/json': ['.json'] } }] });
        const writable = await handle.createWritable();
        await writable.write(json); await writable.close();
        setSavedPath(handle.name ?? suggestedName); return;
      } catch (_) {}
    }
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([json], { type: 'application/json' }));
    a.download = suggestedName; a.click(); URL.revokeObjectURL(a.href);
    setSavedPath(suggestedName);
  };

  const copyCmd = (cmd: string) => {
    navigator.clipboard.writeText(cmd); setCopiedCmd(cmd);
    setTimeout(() => setCopiedCmd(null), 2000);
  };

  // ── Pipeline commands ─────────────────────────────────────────────────────
  const base      = videoName.replace(/\.[^.]+$/, '');
  const demoName  = genDemoName || base.replace(/_raw$/, '');
  const annFile   = savedPath ? `output/video_demo/${savedPath}` : `output/video_demo/${base}_blur_annotations.json`;
  const rawVideo  = `output/video_demo/${base}_raw.mp4`;
  const blurCmd   = `python3 artifacts/video_demo_scripts/add_blur.py \\\n  ${annFile} \\\n  ${rawVideo} \\\n  /tmp/${base}_blurred.mp4`;
  const capCmd    = `python3 artifacts/video_demo_scripts/add_captions.py \\\n  ${demoName} \\\n  /tmp/${base}_blurred.mp4 \\\n  output/video_demo/${demoName}.mp4`;
  const genAnnotPath = genAnnotName ? `output/video_demo/${genAnnotName}` : `output/video_demo/${savedPath ?? `${base}_final.json`}`;
  const finalCmd  = `cd ~/work/drschmoeai && python artifacts/video_demo_scripts/generate_final.py \\\n  ${genAnnotPath} \\\n  ${demoName}`;

  // ── Render ───────────────────────────────────────────────────────────────
  const sel = regions.find(r => r.id === selectedId);
  const openCount = regions.filter(r => r.frameEnd === null).length;
  const trackingPct = totalFrames > 0 ? (trackingFrame / totalFrames) * 100 : 0;
  const cursor = drawMode ? 'crosshair' : isTracking ? 'wait'
    : interaction ? (interaction.handle === 'move' ? 'grabbing' : HANDLE_CURSORS[interaction.handle])
    : hoverHandle ? HANDLE_CURSORS[hoverHandle] : 'default';

  return (
    <Page>
      <input ref={fileInputRef} type="file" accept="video/*" style={{ display: 'none' }} onChange={onFileChange} />
      <input ref={loadAnnRef} type="file" accept=".json" style={{ display: 'none' }}
        onChange={e => {
          const file = e.target.files?.[0]; if (!file) return;
          const reader = new FileReader();
          reader.onload = ev => loadAnnotationsFromJson(ev.target!.result as string);
          reader.readAsText(file); e.target.value = '';
        }} />
      <TopBar>
        <TopTitle>Video Redactor</TopTitle>
        <span style={{ fontSize: 11, color: '#475569' }}>localhost only</span>
        {videoUrl && <span style={{ fontSize: 11, color: '#64748b', marginLeft: 4 }}>{videoName} — {videoDims.w}×{videoDims.h} — {totalFrames} frames @ {fps}fps</span>}
        {videoUrl && <SmBtn $color="#64748b" style={{ marginLeft: 'auto' }} onClick={() => fileInputRef.current?.click()}>Change Video</SmBtn>}
      </TopBar>

      <Body>
        <VideoArea>
          {!videoUrl ? (
            <DropArea data-drag={isDragOver ? 'true' : 'false'}
              onClick={() => fileInputRef.current?.click()}
              onDragOver={e => { e.preventDefault(); setIsDragOver(true); }}
              onDragLeave={() => setIsDragOver(false)} onDrop={onDrop}>
              <div style={{ fontSize: 40 }}>🎬</div>
              <div>Click to load video, or drag and drop</div>
              <div style={{ fontSize: 12, color: '#475569' }}>MP4, MOV, any browser-supported format</div>
            </DropArea>
          ) : (
            <VideoWrapper $aspect={videoAspect}>
              <StyledVideo ref={videoRef} src={videoUrl} />
              <Overlay ref={canvasRef} style={{ cursor }}
                onMouseDown={onMouseDown} onMouseMove={onMouseMove}
                onMouseUp={onMouseUp} onMouseLeave={onMouseLeave} />
            </VideoWrapper>
          )}
          {videoUrl && (
            <FrameBar>
              <IBtn onClick={() => seekToFrame(0)} disabled={isTracking}>⏮</IBtn>
              <IBtn onClick={() => seekToFrame(currentFrame - Math.max(1, Math.round(stepInterval * fps)))} disabled={isTracking} title={`-${stepInterval}s`}>◀◀</IBtn>
              <IBtn onClick={() => seekToFrame(currentFrame - 1)} disabled={isTracking} title="-1 frame">◀</IBtn>
              <Scrubber type="range" min={0} max={Math.max(0, totalFrames - 1)}
                value={currentFrame} disabled={isTracking}
                onChange={e => seekToFrame(Number(e.target.value))} />
              <IBtn onClick={() => seekToFrame(currentFrame + 1)} disabled={isTracking} title="+1 frame">▶</IBtn>
              <IBtn onClick={() => seekToFrame(currentFrame + Math.max(1, Math.round(stepInterval * fps)))} disabled={isTracking} title={`+${stepInterval}s`}>▶▶</IBtn>
              <IBtn onClick={() => seekToFrame(totalFrames - 1)} disabled={isTracking}>⏭</IBtn>
              <FrameText>{currentFrame} / {totalFrames - 1} &nbsp;({fmtTime(currentFrame / fps)})</FrameText>
              <FpsInput type="number" value={fps} min={1} max={120} onChange={e => setFps(Number(e.target.value) || 30)} />
              <span style={{ fontSize: 10, color: '#64748b' }}>fps</span>
              <StepSelect value={stepInterval} onChange={e => setStepInterval(Number(e.target.value))} title="◀◀/▶▶ step">
                {[0.5, 1, 2, 5, 10].map(v => <option key={v} value={v}>{v}s</option>)}
              </StepSelect>
            </FrameBar>
          )}
        </VideoArea>

        <Sidebar>
          <SideTop>
            {!videoUrl ? (
              <Btn onClick={() => fileInputRef.current?.click()}>Load Video</Btn>
            ) : isTracking ? (
              <TrackBar>
                <div style={{ fontSize: 12, color: '#f97316', fontWeight: 600 }}>Tracking... frame {trackingFrame} / {totalFrames}</div>
                <TrackProgress $pct={trackingPct} />
                <Btn $v="red" onClick={() => { abortTracking.current = true; }}>Stop Tracking</Btn>
              </TrackBar>
            ) : (
              <>
                <Btn $v="orange" onClick={() => loadAnnRef.current?.click()}>
                  ↑ Load Annotations JSON
                </Btn>
                <Btn $v={drawMode ? 'active' : 'primary'}
                  onClick={() => { setDrawMode(d => !d); setDraftRect(null); setDragStart(null); }}>
                  {drawMode ? '✕ Cancel  (Esc)' : '+ Draw Redact Region'}
                </Btn>
                {sel && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                    <div style={{ fontSize: 10, color: '#475569' }}>
                      <span style={{ color: sel.color, fontWeight: 700 }}>{sel.label}</span>
                      {' '}frames {sel.frameStart}–{sel.frameEnd ?? '?'}
                      {sel.keyframes && <span style={{ color: '#64748b' }}> · {sel.keyframes.length} kf</span>}
                    </div>
                    {sel.frameEnd === null && <EndHereBtn onClick={endHere}>✕ End region at frame {currentFrame}</EndHereBtn>}
                    <Row>
                      <SmBtn $color="#22c55e" onClick={markIn} style={{ flex: 1, textAlign: 'center' }}>Mark In ({currentFrame})</SmBtn>
                      <SmBtn $color="#ef4444" onClick={markOut} style={{ flex: 1, textAlign: 'center' }}>Mark Out ({currentFrame})</SmBtn>
                    </Row>
                    <SmBtn $color="#f97316" style={{ width: '100%', textAlign: 'center' }}
                      onClick={() => trackRegionForward(sel.id, { ...sel, frameStart: currentFrame })}>
                      ↻ Re-track from frame {currentFrame}
                    </SmBtn>
                  </div>
                )}
                <Btn $v="green" onClick={saveAnnotations} disabled={regions.length === 0}>↓ Save Annotations JSON</Btn>
                {openCount > 0 && <div style={{ fontSize: 10, color: '#f97316' }}>{openCount} open region{openCount > 1 ? 's' : ''} — end before saving</div>}
                {savedPath && <div style={{ fontSize: 10, color: '#22c55e' }}>Saved: {savedPath}</div>}
                <Hint>
                  Load JSON → boxes appear on video.{'\n'}
                  Step with ◀◀/▶▶ (set interval in dropdown).{'\n'}
                  Drag box to move · drag corners to resize.{'\n'}
                  Each drag adds a keyframe (x, y, w, h).{'\n'}
                  Save → new JSON with all keyframes merged.{'\n'}
                  ← / → = 1 frame,  Shift = step interval.
                </Hint>
              </>
            )}
          </SideTop>

          <SideSectionLabel>Regions ({regions.length})</SideSectionLabel>
          <RegionScroll>
            {regions.length === 0 && videoUrl && (
              <div style={{ fontSize: 12, color: '#334155', textAlign: 'center', marginTop: 20, lineHeight: 1.7 }}>
                No regions yet.<br />Click "+ Draw Redact Region"<br />then drag on the video.
              </div>
            )}
            {regions.map(r => {
              const isOpen = r.frameEnd === null;
              const state = getStateAtFrame(r, currentFrame);
              return (
                <RegionItem key={r.id} $sel={r.id === selectedId} $color={r.color} $open={isOpen}
                  onClick={() => setSelectedId(r.id === selectedId ? null : r.id)}>
                  <RHead>
                    <Dot $c={r.color} />
                    <RInput value={r.label} placeholder="label"
                      onChange={e => { e.stopPropagation(); updateLabel(r.id, e.target.value); }}
                      onClick={e => e.stopPropagation()} />
                    {isOpen && <OpenBadge>OPEN</OpenBadge>}
                    <SmBtn $color="#ef4444" onClick={e => { e.stopPropagation(); deleteRegion(r.id); }}>✕</SmBtn>
                  </RHead>
                  <RMeta>{
                    `frames ${r.frameStart}–${isOpen ? '?' : r.frameEnd}  (${fmtTime(r.frameStart / fps)}–${isOpen ? 'open' : fmtTime(r.frameEnd! / fps)})\n` +
                    `x:${state.x} y:${state.y}  w:${state.w} h:${state.h}` +
                    (r.keyframes && r.keyframes.length > 0 ? `\n${r.keyframes.length} keyframes` : '')
                  }</RMeta>
                  {r.id === selectedId && !isTracking && (
                    <RActions>
                      <SmBtn $color="#22c55e" onClick={e => { e.stopPropagation(); markIn(); }}>Mark In</SmBtn>
                      <SmBtn $color="#ef4444" onClick={e => { e.stopPropagation(); markOut(); }}>Mark Out</SmBtn>
                      {isOpen && <SmBtn $color="#ef4444" onClick={e => { e.stopPropagation(); endHere(); }}>End here</SmBtn>}
                      <SmBtn onClick={e => { e.stopPropagation(); seekToFrame(r.frameStart); }}>Go In</SmBtn>
                      {!isOpen && <SmBtn onClick={e => { e.stopPropagation(); seekToFrame(r.frameEnd!); }}>Go Out</SmBtn>}
                      <SmBtn $color="#f97316" onClick={e => { e.stopPropagation(); trackRegionForward(r.id, { ...r, frameStart: currentFrame }); }}>↻ Re-track</SmBtn>
                    </RActions>
                  )}
                </RegionItem>
              );
            })}
          </RegionScroll>

          {videoUrl && (
            <PipelineBox>
              <div style={{ fontSize: 10, fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.07em' }}>Pipeline Commands</div>
              <div style={{ fontSize: 10, color: '#64748b' }}>1. Blur</div>
              <CmdField>{blurCmd}</CmdField>
              <CopySmBtn onClick={() => copyCmd(blurCmd.replace(/\\\n\s+/g, ' '))}>{copiedCmd === blurCmd.replace(/\\\n\s+/g, ' ') ? '✓ Copied' : 'Copy'}</CopySmBtn>
              <div style={{ fontSize: 10, color: '#64748b' }}>2. Captions</div>
              <CmdField>{capCmd}</CmdField>
              <CopySmBtn onClick={() => copyCmd(capCmd.replace(/\\\n\s+/g, ' '))}>{copiedCmd === capCmd.replace(/\\\n\s+/g, ' ') ? '✓ Copied' : 'Copy'}</CopySmBtn>
            </PipelineBox>
          )}

          <GenerateBox>
            <GenLabel>Generate Final Video</GenLabel>
            <input type="file" accept=".json" id="gen-json-input" style={{ display: 'none' }}
              onChange={e => {
                const file = e.target.files?.[0]; if (!file) return;
                const reader = new FileReader();
                reader.onload = ev => {
                  try {
                    const parsed = JSON.parse(ev.target!.result as string);
                    setGenAnnotName(file.name);
                    if (parsed.demo && !genDemoName) setGenDemoName((parsed.demo as string).replace(/_raw$/, ''));
                  } catch {}
                };
                reader.readAsText(file); e.target.value = '';
              }} />
            <Btn onClick={() => document.getElementById('gen-json-input')?.click()} style={{ fontSize: 12 }}>
              {genAnnotName ? `JSON: ${genAnnotName}` : 'Load Annotations JSON'}
            </Btn>
            <div style={{ fontSize: 10, color: '#334155' }}>Demo name</div>
            <GenInput value={genDemoName} onChange={e => setGenDemoName(e.target.value)} placeholder="video_demo_1" />
            <CmdField>{finalCmd}</CmdField>
            <CopySmBtn onClick={() => copyCmd(finalCmd.replace(/\\\n\s+/g, ' '))}>{copiedCmd === finalCmd.replace(/\\\n\s+/g, ' ') ? '✓ Copied' : 'Copy command'}</CopySmBtn>
          </GenerateBox>
        </Sidebar>
      </Body>
    </Page>
  );
};

export default VideoRedactor;
